import { Role, isRole } from '@worklogix/shared';
import { useAuth } from '@worklogix/web-common';

/**
 * Extract role strings from an Entra id-token `roles` claim without assuming a
 * concrete claim type (MSAL types it loosely). Returns `undefined` when the
 * claim is absent — which we treat as "unknown", not "no roles".
 */
function readRoleClaim(claims: unknown): string[] | undefined {
  if (typeof claims !== 'object' || claims === null || !('roles' in claims)) {
    return undefined;
  }
  const roles = (claims as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) {
    return undefined;
  }
  return roles.filter((r): r is string => typeof r === 'string');
}

/**
 * True when the signed-in user may see the supervisor/admin time surfaces.
 *
 * The API is the authoritative gate (Manager/Admin only, enforced server-side);
 * this hook only decides whether to SHOW the nav/routes. It is deliberately
 * fail-OPEN when the `roles` claim is absent (older tokens, or tokens whose app
 * roles are not mapped) — the user will simply hit a 403 on the protected calls
 * rather than being silently locked out of a surface they may in fact own. It
 * hides the surfaces only when we can POSITIVELY determine the user is neither a
 * Manager nor an Admin.
 */
export function useIsManager(): boolean {
  const { account } = useAuth();
  const roles = readRoleClaim(account?.idTokenClaims);

  // Claim absent → unknown → let the API decide (show the surfaces).
  if (roles === undefined) {
    return true;
  }

  return roles.some((r) => isRole(r) && (r === Role.Manager || r === Role.Admin));
}
